
import { EnvDir } from "./com/comEnvironmentFileSystem";
import Utils from "../shared/utils";

import { ModelManager } from "../shared/modelManager";

// models to loading from Server
import { DataModel, SyncModel, SyncSingleDataModel } from "../shared/models/syncModel";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { CoreCOMService } from "./com.service";

export class ServerCallHelper<T extends DataModel> {
  public RequestData: SyncModel = null;
  public FunctionName: string = null;
  public SyncSingleDataModelConstructor: new () => SyncSingleDataModel<T> = null;

  public constructor(requestData: SyncModel, functionName: string, syncSingleDataModelConstructor: new () => SyncSingleDataModel<T>) {
    this.RequestData = requestData;
    this.FunctionName = functionName;
    this.SyncSingleDataModelConstructor = syncSingleDataModelConstructor;
  }
}

/** SingelDataModel Service for handle Data on the Storage, reduce the usage of RAM */

export class SingelDataModelService {

  /** GetObjectObserverByInjectionHelper
   * 
   * @param model_constructor DataModel Construktor
   * @param dir Ordner im storage
   * @param dm_key DataModel Key
   * @param helper Helper
   * @param withServerCall erlauben daten vom server zu hollen, wenn notwendig (siehe dazu refreshAfterInit und oldByError)
   * @param serverCall abruf der daten vom server
   * @param refreshAfterInit auch wenn daten im storage liegen, serverCall durchführen (withServerCall muss true sein)
   * @param oldByError ermöglicht bei fehler auch die alten daten aus dem Storage zurückzugeben, sonst ein fehler
   */
  public static GetDataModel<T extends DataModel>(model_constructor: new () => T, dir: string, dm_key: (string | number), helper: CircularInjectionHelper, withServerCall: boolean, serverCall: ServerCallHelper<T>, refreshAfterInit: boolean, oldByError: boolean): Promise<T> {

    const manager = new ModelManager(model_constructor, helper.coreLogService);

    return new Promise<T>((resolve, reject) => {
      SingelDataModelService._ReadManagerFromStorage(dir, dm_key, manager, helper)
        .then((storedManager) => {
          // ReadManager liefer null, wenn der Manager nicht in im Storage vorliegt
          // in diesem Fall versucht man die Daten vom Server zu laden,
          // ansonsten wird nur der versuchgemacht die daten zu aktualisieren
          if (!Utils.IsNull(storedManager)) {
            if (withServerCall && refreshAfterInit) {
              if (helper.coreCOMService.environment.networkInformation.isOnline) {
                // versuche die Daten daraufhin (nach resolve) zu aktualisieren
                SingelDataModelService._GetObjectsFromServer(storedManager, serverCall, helper)
                  .then((serverData) => {
                    // wenn server neue daten hat, diese gleich speichern
                    storedManager.extendWithSyncSingleData(serverData);
                    SingelDataModelService._SaveManagerToStorage(dir, dm_key, storedManager, helper)
                      .then(() => {
                        resolve(storedManager.get(dm_key));
                      })
                      .catch((error) => { helper.coreLogService.error("SingelDataModelService.GetDataModel", "_SaveManagerToStorage", error); (oldByError ? resolve(storedManager.get(dm_key)) : reject()); });
                  })
                  .catch((error) => { helper.coreLogService.error("SingelDataModelService.GetDataModel", "_GetObjectsFromServer (mobile)", error); (oldByError ? resolve(storedManager.get(dm_key)) : reject()); });
              } else {
                (oldByError ? resolve(storedManager.get(dm_key)) : reject());
              }
            } else {
              resolve(storedManager.get(dm_key));
            }
          } else {
            if (withServerCall) {
              if (helper.coreCOMService.environment.networkInformation.isOnline) {
                SingelDataModelService._GetObjectsFromServer(manager, serverCall, helper)
                  .then((serverData) => {
                    // wenn server neue daten hat, diese gleich speichern
                    manager.extendWithSyncSingleData(serverData);
                    SingelDataModelService._SaveManagerToStorage(dir, dm_key, manager, helper)
                      .then(() => {
                        resolve(manager.get(dm_key));
                      })
                      .catch((error) => { helper.coreLogService.error("SingelDataModelService.GetDataModel", "_SaveManagerToStorage", error); (oldByError ? resolve(manager.get(dm_key)) : reject()); });
                  })
                  .catch((error) => { helper.coreLogService.error("SingelDataModelService.GetDataModel", "_GetObjectsFromServer (mobile)", error); (oldByError ? resolve(manager.get(dm_key)) : reject()); });
              } else {
                (oldByError ? resolve(manager.get(dm_key)) : reject());
              }
            } else {
              resolve(manager.get(dm_key));
            }
          }
        })
        .catch((error) => { helper.coreLogService.error("SingelDataModelService.GetDataModel", "_ReadObjectsFromStorage", error); reject(); });
    });
  }

  /** versucht den manager aus dem speicher zu lesen */
  private static _ReadManagerFromStorage<T extends DataModel>(dir: string, dm_key: (string | number), manager: ModelManager<T>, helper: CircularInjectionHelper): Promise<ModelManager<T>> {
    return new Promise<ModelManager<T>>((resolve, reject) => {
      SingelDataModelService._ReadManagerFromTypedStorage(EnvDir.Data, dir, dm_key, manager, helper)
        .then((dataStoreManager) => {
          if (!Utils.IsNull(dataStoreManager)) {
            resolve(dataStoreManager);
          } else {
            SingelDataModelService._ReadManagerFromTypedStorage(EnvDir.Assets, dir, dm_key, manager, helper)
              .then((assetsStoreManager) => {
                if (!Utils.IsNull(assetsStoreManager)) {
                  resolve(assetsStoreManager);
                } else {
                  // file dose not exists, return null for special case to get initial data
                  resolve(null);
                }
              })
              .catch((error) => { helper.coreLogService.error("SingelDataModelService._ReadManagerFromStorage", "_ReadManagerFromTypedStorage (assets)", error); reject(); });
          }
        })
        .catch((error) => { helper.coreLogService.error("SingelDataModelService._ReadManagerFromStorage", "_ReadManagerFromTypedStorage (data)", error); reject(); });
    });
  }

  /** versucht den manager typen sicher aus dem speicher zu lesen */
  private static _ReadManagerFromTypedStorage<T extends DataModel>(dir: EnvDir, subDir: string, dm_key: (string | number), manager: ModelManager<T>, helper: CircularInjectionHelper): Promise<ModelManager<T>> {
    return new Promise<ModelManager<T>>((resolve, reject) => {
      // if data not set initial as empty
      if (helper.coreCOMService.environment.client.isMobile) {
        helper.safeFilesystem.getFile(dir, "store/" + subDir, dm_key + ".json")
          .then((fileEntry: FileEntry) => {
            if (Utils.IsNull(fileEntry)) { // not found
              resolve(null); // datei ist nicht vorhanden = datei ist leer
            } else {
              helper.safeFilesystem.readStringFromFileByEntry(fileEntry)
                .then((data) => {
                  if (data.length > 0) {
                    manager.deserialize(data);
                    resolve(manager);
                  } else {
                    resolve(null); // datei ist leer
                  }
                })
                .catch((error) => { helper.coreLogService.error("SingelDataModelService._ReadManagerFromTypedStorage", "readStringFromFileByEntry", error); reject(); });
            }
          })
          .catch((error) => { helper.coreLogService.error("SingelDataModelService._ReadManagerFromTypedStorage", "getFile", error); reject(); });
      } else {
        // Browser hold the changes in browser database IndexedDB
        helper.coreCOMService.environment.indexedDB.get(subDir + "/" + dm_key).then((data) => {
          if (data && data.length > 0) {
            manager.deserialize(data);
            resolve(manager);
          } else {
            resolve(null); // data ist leer order nicht vorhanden
          }
        })
          .catch((error) => { helper.coreLogService.error("SingelDataModelService._ReadManagerFromTypedStorage", "indexedDB.get", error); reject(); });
      }
    });
  }

  /** speichert den manager (wenn notwendig) */
  private static _SaveManagerToStorage<T extends DataModel>(subDir: string, dm_key: (string | number), manager: ModelManager<T>, helper: CircularInjectionHelper): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      if (helper.coreCOMService.environment.client.isMobile) {
        helper.safeFilesystem.createFile(EnvDir.Data, "store/" + subDir, dm_key + ".json")
          .then((fileEntry: FileEntry) => {
            helper.safeFilesystem.writeStringToFileByEntry(fileEntry, manager.serialize(), false)
              .then(() => {
                resolve();
              })
              .catch((error) => { helper.coreLogService.error("SingelDataModelService._SaveManagerToStorage", "writeStringToFileByEntry", error); reject(); });
          })
          .catch((error) => { helper.coreLogService.error("SingelDataModelService._SaveManagerToStorage", "createFile", error); reject(); });
      } else {
        // Browser hold the changes in browser database IndexedDB
        helper.coreCOMService.environment.indexedDB.put(subDir + "/" + dm_key, manager.serialize())
          .then(() => {
            resolve();
          })
          .catch((error) => { helper.coreLogService.error("SingelDataModelService._SaveManagerToStorage", "indexedDB.put", error); reject(); });
      }
    });
  }

  /** fragt die daten vom server ab */
  private static _GetObjectsFromServer<T extends DataModel>(manager: ModelManager<T>, serverCall: ServerCallHelper<T>, helper: CircularInjectionHelper): Promise<SyncSingleDataModel<T>> {
    if (Utils.IsNull(serverCall)) {
      return Promise.reject("no serverCall");
    }
    return new Promise<SyncSingleDataModel<T>>((resolve, reject) => {
      // wenn Daten vorliegen, dann nur aktuallisieren
      if (!Utils.IsNull(manager) && manager.LastSyncAt > serverCall.RequestData.changes_since) {
        serverCall.RequestData.changes_since_ymd = manager.LastSyncAt_ymd;
        serverCall.RequestData.changes_since_hms = manager.LastSyncAt_hms;
      }

      helper.yourAppServer.Api.RequestService(serverCall.SyncSingleDataModelConstructor, serverCall.FunctionName, serverCall.RequestData)
        .then((response) => {
          if (response.OK) {
            // me._helper.coreLogService.debug("SingelDataModelService._GetObjectsFromServer", "RequestService (response OK)", response);
            resolve(response.Data);
          } else {
            helper.coreLogService.error("SingelDataModelService._GetObjectsFromServer", "RequestService (response not OK)", [response, serverCall]);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => {
          helper.coreLogService.error("SingelDataModelService._GetObjectsFromServer", "RequestService (error)", error);
          reject();
        });
    });
  }

  /** SetNewDataModel
   * funktion zur internen nutzung, siehe product_detail_helper
   * es ermöglicht das speichern von abgerufenen daten, falls sie alle auf einmal und nicht einzeln abgerufen wurden
   * @param model_constructor DataModel Construktor
   * @param dir Ordner im storage
   * @param model DataModel
   * @param helper Helper
   * @param overwrite wenn true, ignoriert LastSyncAt und request_at (sollte ausgeführt werden, nur wenn es dazu kommt, das request_at bei der abfrage abweichen kann)
   */
  public static SetNewDataModel<T extends DataModel>(model_constructor: new () => T, dir: string, sync_data: SyncSingleDataModel<T>, helper: CircularInjectionHelper, overwrite: boolean = false): Promise<boolean> {

    const manager = new ModelManager(model_constructor, helper.coreLogService);

    return new Promise<boolean>((resolve, reject) => {
      SingelDataModelService._ReadManagerFromStorage(dir, sync_data.model.DM_Key, manager, helper)
        .then((storedManager) => {
          // ReadManager liefer null, wenn der Manager nicht in im Storage vorliegt
          // in diesem Fall versucht man die Daten vom Server zu laden,
          // ansonsten wird nur der versuchgemacht die daten zu aktualisieren
          if (!Utils.IsNull(storedManager)) {
            storedManager.extendWithSyncSingleData(sync_data, overwrite);
            SingelDataModelService._SaveManagerToStorage(dir, sync_data.model.DM_Key, storedManager, helper)
              .then(() => {
                resolve(true);
              })
              .catch((error) => { helper.coreLogService.error("SingelDataModelService.SetNewDataModel", "_SaveManagerToStorage", error); reject(); });
          } else {
            manager.extendWithSyncSingleData(sync_data);
            SingelDataModelService._SaveManagerToStorage(dir, sync_data.model.DM_Key, manager, helper)
              .then(() => {
                resolve(true);
              })
              .catch((error) => { helper.coreLogService.error("SingelDataModelService.SetNewDataModel", "_SaveManagerToStorage", error); reject(); });
          }
        })
        .catch((error) => { helper.coreLogService.error("SingelDataModelService.SetNewDataModel", "_ReadObjectsFromStorage", error); reject(); });
    });
  }

  /** Synchronisiert die aktuell geladenen Objekte mit dem Server */
  public static SynchronizeWithServer(coreCOMService: CoreCOMService): Promise<void> {
    // check has Internet
    if (!coreCOMService.environment.networkInformation.isOnline) {
      Promise.reject("no internet");
    }
    // todo
    return Promise.reject("not implemented yet");
    // aktuell geladene Observer ermitteln, danach durchlaufen
    // getrennte schleifen um nicht beim durchlaufen verändert zu werden
    // const loadedObjObserver = new Array<string>();
    // for (const key in ObjectObserverService._object_observers) {
    //  if (ObjectObserverService._object_observers.hasOwnProperty(key)) {
    //    loadedObjObserver.push(key);
    //  }
    // }
    //
    // return new Promise<void>(async (resolve) => {
    //  // aktuell geladene Observer durchlaufen
    //  for (const key of loadedObjObserver) {
    //    const objObserver = ObjectObserverService._object_observers[key];
    //    if (objObserver.SynchronizingWithServer) {
    //      // erstens die Serverdaten abholen
    //      await objObserver.RefreshObjectsFromServer();
    //      // zweitens die nicht übertragenen Daten an den Server senden
    //      await objObserver.SynchronizeObjectsWithServer();
    //    }
    //  }
    //  resolve();
    // });
  }

  /** Privat darf nicht genutzt werden, dafür sind die Statischen Functionen */
  private constructor() {
  }

}
